import { TIERS_RESOURCE, normalizeTiersName } from './tiersConstants'

/**
 * Aides communes aux emplacements métier liés au référentiel des tiers
 * (notaire client, notaire programme/lot...). Chaque emplacement fournit un
 * writer(key, value) qui écrit dans son propre stockage (store, objet local...).
 */
export default {
  methods: {
    /**
     * Garde anti-doublon : une fiche du même nom (accents ignorés) existe déjà ?
     * @param {string} name
     * @returns {boolean}
     */
    async tiersAlreadyExists (name) {
      try {
        let { data } = await this.$api.get(TIERS_RESOURCE, {
          searchable_name: name,
          per_page: 10
        })
        const duplicate = (data.items || []).find(
          t => normalizeTiersName(t.name) === normalizeTiersName(name))
        if (duplicate) {
          this.$toasted.global.danger(
            `Une fiche « ${duplicate.name} » existe déjà au référentiel — sélectionnez-la dans la recherche`)
          return true
        }
      } catch (e) {
      }
      return false
    },
    /**
     * Copie la fiche + l'interlocuteur sélectionnés dans l'emplacement
     * @param {function} writer - (key, value)
     * @param {object} interlocuteur - { ...interlocuteur, tiers }
     */
    applyTiersSelection (writer, interlocuteur) {
      const tiers = interlocuteur.tiers || {}
      writer('tiers_id', tiers.pk)
      writer('interlocuteur_id', interlocuteur.pk)
      writer('name', tiers.name)
      writer('phone', interlocuteur.phone || tiers.phone || null)
      writer('email', interlocuteur.email || tiers.email || null)
      const address = tiers.address || {}
      writer('address', {
        line1: address.line1 || null,
        line2: address.line2 || null,
        zip_code: address.zip_code || null,
        city: address.city || null,
        country: address.country || null
      })
    },
    /**
     * Délier vide aussi les champs : évite la recréation immédiate d'un doublon
     * @param {function} writer - (key, value)
     */
    applyTiersUnlink (writer) {
      writer('tiers_id', null)
      writer('interlocuteur_id', null)
      writer('name', null)
      writer('phone', null)
      writer('email', null)
      writer('address', {
        line1: null,
        line2: null,
        zip_code: null,
        city: null,
        country: null
      })
    },
    /**
     * Création si nécessaire (CDC) avec garde anti-doublon. Le nom saisi devient
     * la fiche ET son premier interlocuteur.
     * @returns {object|null} - { ...interlocuteur, tiers } ou null
     */
    async createTiersFromFields ({ name, phone, email, address, type, role }) {
      if (!name) return null
      if (await this.tiersAlreadyExists(name)) return null
      try {
        let { data } = await this.$api.post(TIERS_RESOURCE, {
          name,
          type,
          phone: phone || null,
          email: email || null,
          address: address && address.city ? { ...address } : null,
          interlocuteurs: [{
            last_name: name,
            first_name: null,
            role,
            email: email || null,
            phone: phone || null
          }]
        })
        const { interlocuteurs, ...tiers } = data
        this.$toasted.global.success('Tiers ajouté au référentiel')
        return { ...interlocuteurs[0], tiers }
      } catch (e) {
        this.$toasted.global.danger('Erreur lors de l\'enregistrement !')
        return null
      }
    },
    /**
     * Adresse en une ligne pour l'affichage figé d'un emplacement lié
     * @param {object} address
     * @returns {string|null}
     */
    tiersAddressResume (address) {
      if (!address) return null
      const zipCity = [address.zip_code, address.city].filter(Boolean).join(' ')
      return [address.line1, zipCity, address.country].filter(Boolean).join(', ') || null
    },
    /**
     * Précharge la sélection affichée si l'emplacement est déjà lié
     * @param {object} slot - objet contenant tiers_id / interlocuteur_id
     * @returns {object|null}
     */
    async loadTiersSelection (slot) {
      if (!slot || !slot.tiers_id) return null
      try {
        let { data } = await this.$api.getOne(TIERS_RESOURCE, slot.tiers_id)
        const { interlocuteurs, ...tiers } = data
        const interlocuteur = (interlocuteurs || []).find(i => i.pk === slot.interlocuteur_id)
        if (interlocuteur) return { ...interlocuteur, tiers }
      } catch (e) {
      }
      return null
    }
  }
}
